import { cn } from "@/lib/utils";
import {
  Camera,
  CameraOff,
  CircleEllipsis,
  CircleUserRound,
  Info,
  Maximize,
  Mic,
  MicOff,
  Minimize,
  Monitor,
  MonitorOff,
} from "lucide-react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "../../ui/button";
import {
  AudioTrack,
  LocalParticipant,
  RemoteParticipant,
  Track,
} from "livekit-client";
import { useTrack } from "@/hooks/livekit/useTrackChange";
import CameraArea from "./camera-area";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentRoom,
  setStagedUser,
} from "@/context/features/room/roomSlice";
import { useMicLockListener } from "@/hooks/livekit/useMicLockListener";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import AudioRenderer from "../audio/audio-rendere";
import { useParticipantSpeaking } from "@/hooks/livekit/useParticipantSpeaking";
import { useChangeActionAccess } from "@/hooks/livekit/useChangeActionAccess";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/responsive/useIsMobile";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PatientTimer from "@/components/elements/patient-timer";

interface Props {
  user: RemoteParticipant | LocalParticipant;
  isLocal: boolean;
  isAdmin: boolean;
  roomLength: number;
}

export default function UserWidge({
  user,
  isLocal,
  roomLength = 0,
  isAdmin,
}: Props) {
  const videoRef = useRef<HTMLDivElement | null>(null);
  const videoTrack = useTrack(Track.Source.Camera, user);
  const audioTrack = useTrack<AudioTrack>(Track.Source.Microphone, user);
  const { livekitContext } = useLivekitContext();
  const { room } = livekitContext ?? {};
  const [isUserMicLocked, setIsUserMicLocked] = useState<boolean>(false);
  const [isUserCameraLocked, setIsUserCameraLocked] = useState<boolean>(false);
  const [isUserScreenLocked, setIsUserScreenLocked] = useState<boolean>(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { stagedUserId } = useSelector(selectCurrentRoom);
  const isMobile = useIsMobile();
  const { changeActionAccess } = useChangeActionAccess({ room, user });

  useEffect(() => {
    if (!videoTrack || !videoRef.current) return;
    const el = videoTrack.attach() as HTMLVideoElement;

    Object.assign(el.style, {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
      ...(isLocal ? { transform: "scaleX(-1)" } : {}),
    });

    videoRef.current.innerHTML = "";
    videoRef.current.appendChild(el);
    return () => {
      videoTrack.detach().forEach((e) => e.remove());
    };
  }, [videoTrack, isLocal]);

  const setStageHandler = useCallback(() => {
    if (user?.identity) {
      const payload = stagedUserId === user.identity ? "" : user.identity;
      dispatch(setStagedUser(payload));
    }
  }, [user, stagedUserId, dispatch]);
  const muteRemoteCamera = useCallback(() => {
    changeActionAccess(isUserCameraLocked ? "unlock" : "lock", "cam");
  }, [isUserCameraLocked, changeActionAccess]);
  const toggleScreenAccess = useCallback(() => {
    changeActionAccess(isUserScreenLocked ? "unlock" : "lock", "screen");
  }, [isUserScreenLocked, changeActionAccess]);
  const onMicLock = useCallback(
    async (locked: boolean) => {
      if (!room) return;

      if (isLocal) {
        try {
          await room.localParticipant.setMicrophoneEnabled(!locked);
        } catch (e) {
          console.error("Failed to apply mic lock locally:", e);
        }
      }
    },
    [room, isLocal]
  );
  const actionButtonsCallname = useMemo(
    () =>
      "backdrop-blur-md bg-gray-600/25 hover:bg-gray-600/40 rounded-full h-9 w-9 p-2 text-white",
    []
  );
  useMicLockListener(onMicLock);
  const isSpeaking = useParticipantSpeaking(user);
  const controll_buttons = useMemo(
    () => (
      <Fragment>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                changeActionAccess(isUserMicLocked ? "unlock" : "lock", "mic");
                setIsUserMicLocked((prev) => !prev);
              }}
              className={actionButtonsCallname}
              title={isUserMicLocked ? "Unlock mic" : "Lock mic"}
            >
              {isUserMicLocked ? <MicOff /> : <Mic />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{isUserMicLocked ? t("open_mic") : t("lock_mic")}</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                muteRemoteCamera();
                setIsUserCameraLocked((prev) => !prev);
              }}
              className={actionButtonsCallname}
            >
              {isUserCameraLocked ? <CameraOff /> : <Camera />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{isUserCameraLocked ? t("open_cam") : t("lock_cam")}</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                toggleScreenAccess();
                setIsUserScreenLocked((prev) => !prev);
              }}
              className={actionButtonsCallname}
            >
              {isUserScreenLocked ? <MonitorOff /> : <Monitor />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{isUserScreenLocked ? t("open_screen") : t("lock_screen")}</p>
          </TooltipContent>
        </Tooltip>
      </Fragment>
    ),
    [
      toggleScreenAccess,
      muteRemoteCamera,
      changeActionAccess,
      isUserScreenLocked,
      isUserMicLocked,
      isUserCameraLocked,
      actionButtonsCallname,
      t,
    ]
  );
  const controll = useMemo(
    () =>
      isMobile && roomLength > 2 ? (
        <Popover>
          <PopoverTrigger>
            <Button variant="ghost" size="sm" className={actionButtonsCallname}>
              <CircleEllipsis className="shrink-0" />
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-fit text-foreground flex items-center gap-1 mx-2">
            {controll_buttons}
          </PopoverContent>
        </Popover>
      ) : (
        <div className="flex justify-end flex-wrap gap-1">
          {controll_buttons}
        </div>
      ),
    [controll_buttons, roomLength, actionButtonsCallname, isMobile]
  );
  const name = useMemo(
    () =>
      isMobile ? (
        <Popover>
          <PopoverTrigger
            className={cn(
              "flex items-center gap-1 p-1 px-2 rounded-lg min-w-0 ",
              "backdrop-blur-md text-white bg-gray-600/25"
            )}
          >
            <Info className="shrink-0" />
            <span className=" overflow-hidden">
              {isLocal ? t("me") : t(user?.attributes.roleTag || "attendant")}
            </span>
          </PopoverTrigger>

          <PopoverContent className="w-fit text-foreground flex items-center gap-1 mx-2">
            <span className="text-base font-semibold text-ellipsis">
              {user?.name}
            </span>
          </PopoverContent>
        </Popover>
      ) : (
        <div
          className={cn(
            "p-1 px-1.5 rounded-md w-fit flex items-center gap-1",
            "backdrop-blur-md text-white bg-gray-600/25"
          )}
        >
          <span className="text-base text-ellipsis">{user?.name}</span>
        </div>
      ),
    [isMobile, t, isLocal, user]
  );
  const isPatient = useMemo(
    () =>
      user?.attributes.roleTag === "patient" ||
      user?.attributes.roleTag === "user",
    [user]
  );

  const tag = useMemo(
    () => (
      <div className={cn(isMobile ? "hidden" : "")}>
        {user?.attributes.roleTag === "attendant" ? (
          <Badge variant={"outline"} className="text-primary border-primary">
            {t("attendant")}
          </Badge>
        ) : user?.attributes.roleTag === "physician" ||
          user?.attributes.roleTag === "doctor" ? (
          <Badge
            variant={"outline"}
            className="text-green-500 border-green-500"
          >
            {t("physician")}
          </Badge>
        ) : (
          <Badge
            variant={"outline"}
            className="text-orange-500 border-orange-500 "
          >
            {t("patient")}
          </Badge>
        )}
      </div>
    ),
    [t, user, isMobile]
  );
  return (
    <div
      className={cn(
        "relative h-full w-full overflow-hidden rounded-lg",
        "transition-[box-shadow] duration-200",
        isLocal ? "ring-1 ring-primary" : "ring-0",
        isSpeaking
          ? "ring-2 ring-sky-400 shadow-[0_0_10px_4px_rgba(56,189,248,0.55)]"
          : ""
      )}
    >
      {isSpeaking && (
        <div
          className={cn(
            "pointer-events-none absolute inset-0 rounded-lg overflow-hidden",
            "animate-pulse ring-2 ring-red-400/60"
          )}
        />
      )}

      {!isLocal && <AudioRenderer track={audioTrack} />}

      <div className="p-1 w-full flex gap-2 items-center justify-end z-20 relative">
        {isPatient && (
          <PatientTimer userId={user?.identity?.toString()} isPatient={true} />
        )}

        {tag}
        {roomLength > 2 && (
          <Button
            variant="ghost"
            size="default"
            className={actionButtonsCallname}
            onClick={setStageHandler}
          >
            {stagedUserId === user?.identity ? (
              <Minimize strokeWidth={2} size={24} />
            ) : (
              <Maximize strokeWidth={2} size={24} />
            )}
          </Button>
        )}
      </div>

      <div
        className={cn(
          "w-full h-full flex justify-center items-center absolute top-0 right-0",
          "bg-background"
        )}
      >
        {videoTrack?.mediaStream?.active ? (
          <CameraArea ref={videoRef} />
        ) : (
          <CircleUserRound
            strokeWidth={1}
            className={cn(
              "text-gray-100 dark:text-gray-600",
              "w-20 h-20 md:w-40 md:h-40"
            )}
          />
        )}
      </div>

      <div className="w-full z-20 flex justify-between items-center gap-1 absolute bottom-1 px-1">
        <div className="flex items-center gap-2">{name}</div>
        {!isLocal && isAdmin && controll}
      </div>
    </div>
  );
}
